import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';
import { 
  CreditCard, 
  User, 
  Calendar,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Receipt,
  FileText,
  MessageCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BillingPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [allCustomersLoaded, setAllCustomersLoaded] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [processingCustomer, setProcessingCustomer] = useState(null);
  const [processingAll, setProcessingAll] = useState(false);

  useEffect(() => {
    fetchBillingSummary();
  }, []);

  const fetchBillingSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get summary
      const summaryRes = await axios.get(`${API}/billing/pending-charges-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSummary(summaryRes.data);
      setAllCustomersLoaded(false);

      const topCustomers = summaryRes.data.top_customers || [];
      const normalizedCustomers = topCustomers.map((customer) => ({
        id: customer.customer_id,
        customer_name: customer.customer_name,
        customer_email: customer.customer_email || '',
        sessions: customer.sessions || 0,
        amount: customer.amount || 0
      }));

      setCustomers(normalizedCustomers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      toast.error('Fehler beim Laden der Abrechnungsdaten');
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/customers/${customerId}/pending-charges`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerDetails(response.data);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Fehler beim Laden der Kundendetails');
    }
  };

  const loadAllCustomers = async () => {
    try {
      setCustomersLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/billing/pending-charges-customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allCustomers = response.data.customers || [];
      const normalizedCustomers = allCustomers.map((customer) => ({
        id: customer.customer_id,
        customer_name: customer.customer_name,
        customer_email: customer.customer_email || '',
        sessions: customer.sessions || 0,
        amount: customer.amount || 0
      }));

      setCustomers(normalizedCustomers);
      setAllCustomersLoaded(true);
    } catch (error) {
      console.error('Error loading all customers:', error);
      toast.error('Fehler beim Laden der Kundenliste');
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerDetails(customer.id);
  };

  const handleGenerateInvoice = async (customerId, customerName) => {
    if (!window.confirm(`Möchten Sie wirklich eine Rechnung für ${customerName} erstellen?`)) {
      return;
    }

    try {
      setProcessingCustomer(customerId);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/customers/${customerId}/generate-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Rechnung erfolgreich erstellt! Nummer: ${response.data.invoice_number}`);
      
      // Refresh data
      await fetchBillingSummary();
      setSelectedCustomer(null);
      setCustomerDetails(null);
      
    } catch (error) {
      console.error('Error generating invoice:', error);
      if (error.response?.status === 400) {
        toast.error('Keine offenen Termine zum Abrechnen gefunden');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Rechnung');
      }
    } finally {
      setProcessingCustomer(null);
    }
  };


  const handleSendWhatsAppInvoice = async (customerId, totalAmount) => {
    if (!window.confirm('Möchten Sie die Rechnungsbenachrichtigung per WhatsApp senden?')) {
      return;
    }

    try {
      setProcessingCustomer(customerId);
      const token = localStorage.getItem('token');
      
      // Generate invoice number (or get from last invoice)
      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30); // 30 days from now
      
      const response = await axios.post(
        `${API}/whatsapp/send-invoice-notification`,
        {
          customer_id: customerId,
          invoice_amount: totalAmount,
          invoice_number: invoiceNumber,
          due_date: dueDate.toISOString().split('T')[0]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("✅ Rechnungsbenachrichtigung per WhatsApp gesendet!");
      } else {
        toast.error("❌ Fehler beim Senden der Benachrichtigung");
      }
      
    } catch (error) {
      console.error('WhatsApp Invoice Error:', error);
      const errorMsg = error.response?.data?.detail || "Fehler beim Senden der WhatsApp-Benachrichtigung";
      toast.error(errorMsg);
    } finally {
      setProcessingCustomer(null);
    }
  };


  const handleGenerateAllInvoices = async () => {
    if (!window.confirm(`Möchten Sie wirklich ALLE ${customers.length} Kunden jetzt abrechnen?\n\nEs werden ${summary.total_sessions} Trainings mit einem Gesamtbetrag von CHF ${summary.total_amount?.toFixed(2)} abgerechnet.`)) {
      return;
    }

    try {
      setProcessingAll(true);
      const token = localStorage.getItem('token');
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Process each customer sequentially
      for (const customer of customers) {
        try {
          const response = await axios.post(
            `${API}/customers/${customer.id}/generate-invoice`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          successCount++;
          console.log(`✅ Invoice created for ${customer.first_name} ${customer.last_name}: ${response.data.invoice_number}`);
          
        } catch (error) {
          errorCount++;
          const customerName = `${customer.first_name} ${customer.last_name}`;
          errors.push(`${customerName}: ${error.response?.data?.detail || 'Unbekannter Fehler'}`);
          console.error(`❌ Failed for ${customerName}:`, error);
        }
      }
      
      // Show results
      if (successCount === customers.length) {
        toast.success(`🎉 Alle ${successCount} Rechnungen erfolgreich erstellt!`);
      } else if (successCount > 0) {
        toast.success(`${successCount} von ${customers.length} Rechnungen erstellt.`);
        if (errorCount > 0) {
          toast.error(`${errorCount} Fehler aufgetreten. Siehe Konsole für Details.`);
          console.error('Fehler:', errors);
        }
      } else {
        toast.error('Keine Rechnungen konnten erstellt werden.');
      }
      
      // Refresh data
      await fetchBillingSummary();
      setSelectedCustomer(null);
      setCustomerDetails(null);
      
    } catch (error) {
      console.error('Error in bulk invoice generation:', error);
      toast.error('Fehler bei der Massen-Abrechnung');
    } finally {
      setProcessingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Abrechnungsdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Offene Abrechnungen</h1>
        <p className="text-gray-600 mt-2">Ende Monat Kunden - Manuelle Abrechnung</p>
      </div>

      {/* Summary Cards */}
      {summary && summary.total_customers > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-orange-800">{summary.total_customers}</p>
                  <p className="text-sm text-orange-600 mt-1">Kunden mit offenen Abrechnungen</p>
                </div>
                <User className="h-12 w-12 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-blue-800">{summary.total_sessions}</p>
                  <p className="text-sm text-blue-600 mt-1">Offene Trainings insgesamt</p>
                </div>
                <Calendar className="h-12 w-12 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-green-800">CHF {summary.total_amount?.toFixed(2)}</p>
                  <p className="text-sm text-green-600 mt-1">Gesamtbetrag offen</p>
                </div>
                <DollarSign className="h-12 w-12 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Action Button */}
      {summary && summary.total_customers > 0 && (
        <Card className="bg-gradient-to-r from-orange-500 to-red-500 border-0 shadow-lg">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="text-white">
                <h3 className="text-xl font-bold mb-1">Massen-Abrechnung</h3>
                <p className="text-orange-100 text-sm">
                  Alle {summary.total_customers} Kunden ({summary.total_sessions} Trainings) gleichzeitig abrechnen
                </p>
              </div>
              <Button
                onClick={handleGenerateAllInvoices}
                disabled={processingAll}
                size="lg"
                className="bg-white text-orange-600 hover:bg-orange-50 font-bold px-8"
              >
                {processingAll ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600 mr-2"></div>
                    Wird verarbeitet...
                  </>
                ) : (
                  <>
                    <Receipt className="mr-2 h-5 w-5" />
                    Alle jetzt abrechnen
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No pending charges */}
      {(!summary || summary.total_customers === 0) && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine offenen Abrechnungen</h3>
            <p className="text-gray-600">Aktuell gibt es keine Kunden mit offenen Ende-Monat Abrechnungen.</p>
          </CardContent>
        </Card>
      )}

      {/* Customer List and Details */}
      {customers.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2">
                  <User size={20} />
                  Kunden Liste ({customers.length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadAllCustomers}
                  disabled={customersLoading || allCustomersLoaded}
                  data-testid="billing-load-all-customers-button"
                >
                  {customersLoading ? 'Lädt...' : allCustomersLoaded ? 'Alle geladen' : 'Alle laden'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedCustomer?.id === customer.id
                        ? 'bg-teal-50 border-teal-300'
                        : 'bg-white hover:bg-gray-50 border-gray-200'
                    }`}
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {customer.customer_name}
                        </p>
                        <p className="text-sm text-gray-600">{customer.customer_email || '-'}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-sm text-orange-600">
                            {customer.sessions || 0} Trainings
                          </span>
                          <span className="text-sm font-semibold text-orange-800">
                            CHF {customer.amount?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      </div>
                      <div>
                        {selectedCustomer?.id === customer.id ? (
                          <div className="h-2 w-2 bg-teal-600 rounded-full"></div>
                        ) : (
                          <AlertCircle className="h-5 w-5 text-orange-500" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Customer Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText size={20} />
                {selectedCustomer ? 'Details' : 'Kunden auswählen'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedCustomer && (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p>Wählen Sie einen Kunden aus der Liste</p>
                </div>
              )}

              {selectedCustomer && customerDetails && (
                <div className="space-y-4">
                  {/* Customer Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg mb-2">
                      {selectedCustomer.customer_name}
                    </h3>
                    <p className="text-sm text-gray-600">{selectedCustomer.customer_email || '-'}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-gray-500">Offene Trainings</p>
                        <p className="text-lg font-bold text-orange-600">
                          {customerDetails.total_count}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Gesamtbetrag</p>
                        <p className="text-lg font-bold text-orange-800">
                          CHF {customerDetails.total_amount?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Hinweis: Kunde wird über Zahler abgerechnet */}
                  {customerDetails.billed_via_payer_name && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="billing-payer-hint">
                      👨‍👩‍👧 Diese Trainings werden über die <strong>Sammelrechnung von {customerDetails.billed_via_payer_name}</strong> abgerechnet.
                    </div>
                  )}

                  {/* Appointments List */}
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">
                      Offene Trainings ({customerDetails.appointments?.length || 0}):
                    </h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {customerDetails.appointments?.map((appt) => (
                        <div
                          key={appt.id}
                          className="p-3 bg-white border border-gray-200 rounded-lg"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {appt.service_name}
                                {appt.billed_for && (
                                  <span className="ml-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid={`billing-appointment-billedfor-${appt.id}`}>
                                    — für {appt.billed_for}
                                  </span>
                                )}
                              </p>
                              {appt.trainer_name && (
                                <p className="text-sm text-gray-600">Trainer: {appt.trainer_name}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(appt.start_time || appt.date).toLocaleDateString('de-CH', {
                                  weekday: 'short',
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-orange-800">
                                CHF {appt.price?.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Produktverkäufe (Shakes etc.) — fließen in die Gesamtsumme ein */}
                  {customerDetails.products?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">
                        Offene Produkte ({customerDetails.products.length}):
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {customerDetails.products.map((p) => (
                          <div
                            key={p.id}
                            className="p-3 bg-white border border-gray-200 rounded-lg"
                            data-testid={`billing-product-${p.id}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  🥤 {p.product_name}
                                  {p.quantity > 1 && <span className="text-gray-500"> × {p.quantity}</span>}
                                  {p.billed_for && (
                                    <span className="ml-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid={`billing-product-billedfor-${p.id}`}>
                                      — für {p.billed_for}
                                    </span>
                                  )}
                                </p>
                                {p.date && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(p.date).toLocaleDateString('de-CH', {
                                      day: '2-digit', month: '2-digit', year: 'numeric'
                                    })}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-orange-800">
                                  CHF {p.price?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="pt-4 border-t space-y-2">
                    <Button
                      onClick={() => handleGenerateInvoice(selectedCustomer.id, selectedCustomer.customer_name)}
                      disabled={processingCustomer === selectedCustomer.id}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      {processingCustomer === selectedCustomer.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Wird verarbeitet...
                        </>
                      ) : (
                        <>
                          <Receipt className="mr-2 h-4 w-4" />
                          Jetzt abrechnen (CHF {customerDetails.total_amount?.toFixed(2)})
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleSendWhatsAppInvoice(selectedCustomer.id, customerDetails.total_amount)}
                      disabled={processingCustomer === selectedCustomer.id}
                      variant="outline"
                      className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Rechnung per WhatsApp senden
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Es wird eine Rechnung erstellt und alle Termine als "abgerechnet" markiert
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
